import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { authFetch } from '../api/orchestrator';

interface ResponseTimeData {
  chat_id: string;
  phone_number: string;
  name: string | null;
  chat_created_at: string;
  entity_id: string | null;
  first_message_at: string;
  first_response_at: string | null;
  response_time_seconds: number | null;
  response_bucket: string;
  response_bucket_order: number;
}

interface DailyTrendData {
  chat_date: string;
  total_chats: number;
  avg_response_time: number;
  median_response_time: number;
}

interface SummaryData {
  totalClientInitiatedChats: number;
  avgResponseTime: number;
  medianResponseTime: number;
  bucketDistribution: {
    under_1_min: number;
    '1_to_5_min': number;
    '5_to_15_min': number;
    '15_to_60_min': number;
    over_1_hour: number;
  };
}

interface AnalyticsResponse {
  ok: boolean;
  data: {
    chats: ResponseTimeData[];
    summary: SummaryData;
    dailyTrend: DailyTrendData[];
  };
}

const COLORS = ['#00C49F', '#0088FE', '#FFBB28', '#FF8042', '#FF6B6B'];

const BUCKET_LABELS: Record<string, string> = {
  under_1_min: 'Under 1 min',
  '1_to_5_min': '1-5 min',
  '5_to_15_min': '5-15 min',
  '15_to_60_min': '15-60 min',
  over_1_hour: 'Over 1 hour'
};

const ResponseTimeDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AnalyticsResponse['data'] | null>(null);
  const [days, setDays] = useState(30);
  const [selectedEntity, setSelectedEntity] = useState<string>('all');
  const [entities, setEntities] = useState<Array<{ id: string; name: string }>>([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = { days };
      if (selectedEntity !== 'all') {
        params.entityId = selectedEntity;
      }

      const queryString = new URLSearchParams(params).toString();
      const url = `/api/analytics/response-times${queryString ? '?' + queryString : ''}`;

      const response = await authFetch(url);
      const data = await response.json();

      if (data.ok) {
        setData(data.data);
      } else {
        setError(data.error || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [days, selectedEntity]);

  // Fetch entities for filter
  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const response = await authFetch('/api/entities');
        const data = await response.json();
        if (data.ok) {
          setEntities(data.entities || data.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch entities:', err);
      }
    };
    fetchEntities();
  }, []);

  const formatTime = (seconds: number | null): string => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const formatDateShort = (dateStr: string): string => {
    const d = new Date(dateStr);
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear().toString().slice(-2);
    return `${day}-${MONTHS[d.getMonth()]}-${year}`;
  };

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear().toString().slice(-2);
    const hh = d.getHours().toString().padStart(2, '0');
    const mm = d.getMinutes().toString().padStart(2, '0');
    return `${day}-${MONTHS[d.getMonth()]}-${year} ${hh}:${mm}`;
  };

  const bucketChartData = data ? Object.entries(data.summary.bucketDistribution).map(([key, value]) => ({
    name: BUCKET_LABELS[key] || key,
    value,
    fill: COLORS[Object.keys(BUCKET_LABELS).indexOf(key)]
  })) : [];

  const dailyTrendChartData = data?.dailyTrend.map(d => ({
    date: formatDateShort(d.chat_date),
    'Total Chats': d.total_chats,
    'Avg Response (min)': Math.round((d.avg_response_time || 0) / 60),
    'Median Response (min)': Math.round((d.median_response_time || 0) / 60)
  })) || [];

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Response Time Analytics</h2>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <h2>Response Time Analytics</h2>
        <div style={{ color: 'red', padding: '10px', backgroundColor: '#fee', borderRadius: '4px' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '20px' }}>Response Time Analytics Dashboard</h2>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        alignItems: 'center'
      }}>
        <div>
          <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Time Period:</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>

        <div>
          <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Entity:</label>
          <select
            value={selectedEntity}
            onChange={(e) => setSelectedEntity(e.target.value)}
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="all">All Entities</option>
            {entities.map(entity => (
              <option key={entity.id} value={entity.id}>{entity.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      {data && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          <div style={{
            padding: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #0088FE'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>Total Client-Initiated Chats</h3>
            <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#0088FE' }}>
              {data.summary.totalClientInitiatedChats}
            </p>
          </div>

          <div style={{
            padding: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #00C49F'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>Average Response Time</h3>
            <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#00C49F' }}>
              {formatTime(data.summary.avgResponseTime)}
            </p>
          </div>

          <div style={{
            padding: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #FFBB28'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>Median Response Time</h3>
            <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#FFBB28' }}>
              {formatTime(data.summary.medianResponseTime)}
            </p>
          </div>
        </div>
      )}

      {/* Charts Row 1 */}
      {data && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '20px',
          marginBottom: '30px'
        }}>
          {/* Daily Trend Chart */}
          <div style={{
            padding: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginBottom: '15px' }}>Daily Response Time Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyTrendChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: 'Number of Chats', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} label={{ value: 'Response Time (min)', angle: 90, position: 'insideRight', style: { fontSize: 10 } }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line yAxisId="left" type="monotone" dataKey="Total Chats" stroke="#0088FE" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="Avg Response (min)" stroke="#00C49F" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="Median Response (min)" stroke="#FFBB28" strokeWidth={2} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Response Time Distribution Pie Chart */}
          <div style={{
            padding: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginBottom: '15px' }}>Response Time Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={bucketChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  style={{ fontSize: 10 }}
                >
                  {bucketChartData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Bar Chart - Bucket Distribution */}
      {data && (
        <div style={{
          padding: '20px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '30px'
        }}>
          <h3 style={{ marginBottom: '15px' }}>Response Time Buckets</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={bucketChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} label={{ value: 'Number of Chats', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
              <Tooltip />
              <Bar dataKey="value" fill="#0088FE" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Data Table */}
      {data && (
        <div style={{
          padding: '20px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginBottom: '15px' }}>Chat Response Details</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Phone</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>First Message</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Response</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Response Time</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Bucket</th>
                </tr>
              </thead>
              <tbody>
                {data.chats.slice(0, 100).map((chat, index) => (
                  <tr key={chat.chat_id} style={{
                    backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9',
                    borderBottom: '1px solid #eee'
                  }}>
                    <td style={{ padding: '12px' }}>{formatDate(chat.chat_created_at)}</td>
                    <td style={{ padding: '12px' }}>{chat.phone_number}</td>
                    <td style={{ padding: '12px' }}>{chat.name || '-'}</td>
                    <td style={{ padding: '12px' }}>{formatDate(chat.first_message_at)}</td>
                    <td style={{ padding: '12px' }}>{chat.first_response_at ? formatDate(chat.first_response_at) : 'No response'}</td>
                    <td style={{ padding: '12px', fontWeight: 'bold' }}>
                      {chat.response_time_seconds ? formatTime(chat.response_time_seconds) : 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: COLORS[(chat.response_bucket_order || 1) - 1],
                        color: '#fff',
                        fontSize: '12px'
                      }}>
                        {BUCKET_LABELS[chat.response_bucket] || chat.response_bucket}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.chats.length > 100 && (
            <p style={{ marginTop: '15px', color: '#666', fontSize: '13px' }}>
              Showing first 100 of {data.chats.length} chats
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ResponseTimeDashboard;
